import type {
  ChatbotQuestion,
  ChatbotQuestionStepType,
  FreelancerType,
  ServiceCatalogBenefitTag,
  ServiceCatalogItem,
  ServiceCatalogLead,
  ServiceCatalogLeadStatus,
  ServiceCatalogPageContent,
  chatbot_question_seed,
  freelancer_type_seed,
  service_catalog_benefit_tag_seed,
  service_catalog_item_seed,
} from '@pigeons/shared-service-catalog';
import { serviceCatalogRepository } from '../repositories/serviceCatalogRepository';

const get_next_sort_order = (items: { sort_order: number }[]): number => {
  if (items.length === 0) {
    return 10;
  }

  return Math.max(...items.map(item => item.sort_order)) + 10;
};

const build_reorder_payload = (items: { id: string }[]) =>
  items.map((item, index) => ({
    id: item.id,
    sort_order: (index + 1) * 10,
  }));

const build_question_reorder_payload = (items: { id: string }[]) =>
  items.map((item, index) => ({
    id: item.id,
    question_order: (index + 1) * 10,
  }));

const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const generate_client_key = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export const serviceCatalogService = {
  slugify,

  listFreelancerTypes: (): Promise<FreelancerType[]> =>
    serviceCatalogRepository.listFreelancerTypes(),
  createFreelancerType: (items: FreelancerType[]): Promise<FreelancerType> =>
    serviceCatalogRepository.createFreelancerType({
      name_fr: '',
      name_en: '',
      emoji: '👤',
      color: '#1C6F60',
      sort_order: get_next_sort_order(items),
    }),
  updateFreelancerType: (
    id: string,
    payload: Partial<freelancer_type_seed>
  ): Promise<FreelancerType> => serviceCatalogRepository.updateFreelancerType(id, payload),
  deleteFreelancerType: (id: string): Promise<void> =>
    serviceCatalogRepository.deleteFreelancerType(id),
  reorderFreelancerTypes: (items: FreelancerType[]): Promise<void> =>
    serviceCatalogRepository.reorderFreelancerTypes(build_reorder_payload(items)),

  listServiceCatalogItems: (): Promise<ServiceCatalogItem[]> =>
    serviceCatalogRepository.listServiceCatalogItems(),
  createServiceCatalogItem: (items: ServiceCatalogItem[]): Promise<ServiceCatalogItem> =>
    serviceCatalogRepository.createServiceCatalogItem({
      slug: `nouveau-service-${Date.now()}`,
      badge_label_fr: null,
      badge_label_en: null,
      category_fr: null,
      category_en: null,
      name_fr: 'Nouveau service',
      name_en: 'New service',
      short_description_fr: null,
      short_description_en: null,
      full_description_fr: null,
      full_description_en: null,
      chatbot_greeting_fr: null,
      chatbot_greeting_en: null,
      chatbot_intro_fr: null,
      chatbot_intro_en: null,
      active: false,
      sort_order: get_next_sort_order(items),
    }),
  updateServiceCatalogItem: (
    id: string,
    payload: Partial<service_catalog_item_seed>
  ): Promise<ServiceCatalogItem> => serviceCatalogRepository.updateServiceCatalogItem(id, payload),
  deleteServiceCatalogItem: (id: string): Promise<void> =>
    serviceCatalogRepository.deleteServiceCatalogItem(id),
  reorderServiceCatalogItems: (items: ServiceCatalogItem[]): Promise<void> =>
    serviceCatalogRepository.reorderServiceCatalogItems(build_reorder_payload(items)),

  listFreelancerTypeIdsForItem: (serviceCatalogItemId: string): Promise<string[]> =>
    serviceCatalogRepository.listFreelancerTypeIdsForItem(serviceCatalogItemId),
  setFreelancerTypeIdsForItem: (
    serviceCatalogItemId: string,
    freelancerTypeIds: string[]
  ): Promise<void> =>
    serviceCatalogRepository.setFreelancerTypeIdsForItem(serviceCatalogItemId, freelancerTypeIds),

  listBenefitTags: (serviceCatalogItemId: string): Promise<ServiceCatalogBenefitTag[]> =>
    serviceCatalogRepository.listBenefitTags(serviceCatalogItemId),
  createBenefitTag: (
    serviceCatalogItemId: string,
    items: ServiceCatalogBenefitTag[]
  ): Promise<ServiceCatalogBenefitTag> =>
    serviceCatalogRepository.createBenefitTag({
      service_catalog_item_id: serviceCatalogItemId,
      label_fr: '',
      label_en: '',
      sort_order: get_next_sort_order(items),
    }),
  updateBenefitTag: (
    id: string,
    payload: Partial<service_catalog_benefit_tag_seed>
  ): Promise<ServiceCatalogBenefitTag> => serviceCatalogRepository.updateBenefitTag(id, payload),
  deleteBenefitTag: (id: string): Promise<void> => serviceCatalogRepository.deleteBenefitTag(id),
  reorderBenefitTags: (items: ServiceCatalogBenefitTag[]): Promise<void> =>
    serviceCatalogRepository.reorderBenefitTags(build_reorder_payload(items)),

  listChatbotQuestions: (serviceCatalogItemId: string): Promise<ChatbotQuestion[]> =>
    serviceCatalogRepository.listChatbotQuestions(serviceCatalogItemId),
  createChatbotQuestion: (
    serviceCatalogItemId: string,
    items: ChatbotQuestion[],
    stepType: ChatbotQuestionStepType
  ): Promise<ChatbotQuestion> =>
    serviceCatalogRepository.createChatbotQuestion({
      service_catalog_item_id: serviceCatalogItemId,
      question_order: get_next_sort_order(items.map(item => ({ sort_order: item.question_order }))),
      question_text_fr: '',
      question_text_en: '',
      placeholder_fr: null,
      placeholder_en: null,
      client_key: generate_client_key(),
      step_type: stepType,
      optional: false,
      options: stepType === 'choice' ? [] : null,
      show_if: null,
    }),
  updateChatbotQuestion: (
    id: string,
    payload: Partial<chatbot_question_seed>
  ): Promise<ChatbotQuestion> => serviceCatalogRepository.updateChatbotQuestion(id, payload),
  deleteChatbotQuestion: (id: string): Promise<ChatbotQuestion[]> =>
    serviceCatalogRepository.deleteChatbotQuestion(id),
  reorderChatbotQuestions: (items: ChatbotQuestion[]): Promise<void> =>
    serviceCatalogRepository.reorderChatbotQuestions(build_question_reorder_payload(items)),

  listLeads: (): Promise<ServiceCatalogLead[]> => serviceCatalogRepository.listLeads(),
  updateLeadStatus: (id: string, status: ServiceCatalogLeadStatus): Promise<ServiceCatalogLead> =>
    serviceCatalogRepository.updateLeadStatus(id, status),

  getPageContent: (): Promise<ServiceCatalogPageContent | null> =>
    serviceCatalogRepository.getPageContent(),
  updatePageContent: (
    id: string,
    payload: Partial<Omit<ServiceCatalogPageContent, 'id' | 'updated_at'>>
  ): Promise<ServiceCatalogPageContent> => serviceCatalogRepository.updatePageContent(id, payload),
};
